import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API, clearAdminSession, getAdminAuthHeaders } from '../lib/api';
import { AdminDashboardHeader } from '../components/admin/AdminDashboardHeader';
import { AdminStatsCards } from '../components/admin/AdminStatsCards';
import { AdminFiltersBar } from '../components/admin/AdminFiltersBar';
import { AdminPropertyCard } from '../components/admin/AdminPropertyCard';
import { AdminReviewsPanel } from '../components/admin/AdminReviewsPanel';
import { PublicationDialog } from '../components/admin/PublicationDialog';
import { PaymentDialog } from '../components/admin/PaymentDialog';

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') === 'reviews' ? 'reviews' : 'properties';
  const [activeTab, setActiveTab] = useState(initialTab);

  const [properties, setProperties] = useState([]);
  const [modificationRequestsByLicense, setModificationRequestsByLicense] = useState({});
  const [pendingVersionsByLicense, setPendingVersionsByLicense] = useState({});
  const [versionsByLicense, setVersionsByLicense] = useState({});
  const [reviews, setReviews] = useState([]);

  const [loading, setLoading] = useState(true);
  const [recreatingSample, setRecreatingSample] = useState(false);
  const [lastActionMessage, setLastActionMessage] = useState('');
  const [busyReviewId, setBusyReviewId] = useState(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishingLicense, setPublishingLicense] = useState(null);
  const [publishingProperty, setPublishingProperty] = useState(null);
  const [publishUrl, setPublishUrl] = useState('');
  const [publishLoading, setPublishLoading] = useState(false);

  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [editingLicense, setEditingLicense] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [manageLinksByLicense, setManageLinksByLicense] = useState({});
  const [busyManageLinkFor, setBusyManageLinkFor] = useState('');
  const [busyResendFor, setBusyResendFor] = useState('');
  const [busyVersionPublishId, setBusyVersionPublishId] = useState(null);

  const nowLabel = () => new Date().toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const withAdminAuth = useCallback((config = {}) => ({
    ...config,
    headers: {
      ...getAdminAuthHeaders(),
      ...(config.headers || {}),
    },
  }), []);

  const handleAuthError = useCallback((error) => {
    if (error.response?.status === 401) {
      clearAdminSession();
      toast.error('Tu sesion ha expirado. Inicia sesion de nuevo.');
      navigate('/admin/login');
      return true;
    }
    return false;
  }, [navigate]);

  const fetchProperties = useCallback(async () => {
    try {
      setLoading(true);

      const [propertiesResponse, requestsResponse, versionsResponse, reviewsResponse] = await Promise.all([
        axios.get(`${API}/properties/`, withAdminAuth()),
        axios.get(`${API}/modifications/admin/requests`, withAdminAuth()),
        axios.get(`${API}/modifications/admin/versions?limit=2000`, withAdminAuth()),
        axios.get(`${API}/reviews/admin`, withAdminAuth()),
      ]);

      setProperties(propertiesResponse.data);

      const groupedRequests = requestsResponse.data.reduce((acc, item) => {
        const key = item.license_number;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});
      setModificationRequestsByLicense(groupedRequests);

      const groupedVersions = versionsResponse.data.reduce((acc, item) => {
        const key = item.license_number;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      const groupedPendingVersions = versionsResponse.data.reduce((acc, item) => {
        if (item.ops_status !== 'paid_pending_publish') return acc;
        const key = item.license_number;
        if (!acc[key]) acc[key] = [];
        acc[key].push(item);
        return acc;
      }, {});

      setVersionsByLicense(groupedVersions);
      setPendingVersionsByLicense(groupedPendingVersions);
      setReviews(reviewsResponse.data || []);
    } catch (error) {
      console.error('Error fetching properties:', error);
      if (handleAuthError(error)) return;
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  }, [handleAuthError, withAdminAuth]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleDelete = async (licenseNumber) => {
    if (!window.confirm('Estas seguro de eliminar esta propiedad?')) return;

    try {
      await axios.delete(`${API}/properties/${licenseNumber}`, withAdminAuth());
      toast.success('Propiedad eliminada');
      setLastActionMessage(`[${nowLabel()}] Propiedad ${licenseNumber} eliminada`);
      fetchProperties();
    } catch (error) {
      console.error('Error deleting property:', error);
      if (handleAuthError(error)) return;
      toast.error('Error al eliminar propiedad');
    }
  };

  const openPublishDialog = (licenseNumber) => {
    const property = properties.find((item) => item.license_number === licenseNumber);
    if (!property) {
      toast.error('Propiedad no encontrada');
      return;
    }

    if (property.publication?.published) {
      setEditingLicense(licenseNumber);
      setPublishingLicense(null);
      setPublishUrl(property.publication?.netlify_url || '');
    } else {
      setPublishingLicense(licenseNumber);
      setEditingLicense(null);
      setPublishUrl('');
    }

    setPublishingProperty(property);
    setPublishDialogOpen(true);
  };

  const resetPublishDialog = () => {
    setPublishDialogOpen(false);
    setPublishingLicense(null);
    setEditingLicense(null);
    setPublishingProperty(null);
    setPublishUrl('');
  };

  const handleSavePublication = async () => {
    if (!publishUrl.trim()) {
      toast.error('Por favor ingresa una URL');
      return;
    }

    try {
      setPublishLoading(true);
      const license = publishingLicense || editingLicense;

      await axios.post(`${API}/properties/${license}/publish`, {
        netlify_url: publishUrl,
      }, withAdminAuth());

      toast.success(publishingLicense ? 'Propiedad publicada exitosamente' : 'URL actualizada');
      setLastActionMessage(`[${nowLabel()}] ${publishingLicense ? 'Publicada' : 'URL actualizada'} para ${license}`);
      resetPublishDialog();
      fetchProperties();
    } catch (error) {
      console.error('Error publishing property:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'Error al publicar');
    } finally {
      setPublishLoading(false);
    }
  };

  const handleUnpublish = async (licenseNumber) => {
    if (!window.confirm('Desmarcar como publicado?')) return;

    try {
      await axios.post(`${API}/properties/${licenseNumber}/unpublish`, {}, withAdminAuth());
      toast.success('Marcado como no publicado');
      setLastActionMessage(`[${nowLabel()}] Propiedad ${licenseNumber} marcada como no publicada`);
      fetchProperties();
    } catch (error) {
      console.error('Error unpublishing property:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'Error al desmarcar');
    }
  };

  const openPaymentDialog = (licenseNumber) => {
    const property = properties.find((item) => item.license_number === licenseNumber);
    if (!property) return;

    setEditingLicense(licenseNumber);
    const existingAmount = property.payment?.amount;
    setPaymentAmount(existingAmount !== undefined && existingAmount !== null ? Math.round(Number(existingAmount)).toString() : '');
    setPaymentDialogOpen(true);
  };

  const resetPaymentDialog = () => {
    setPaymentDialogOpen(false);
    setEditingLicense(null);
    setPaymentAmount('');
  };

  const handleSavePayment = async () => {
    const parsedAmount = Number(paymentAmount);
    if (!paymentAmount.trim() || Number.isNaN(parsedAmount) || parsedAmount < 0) {
      toast.error('Por favor ingresa un monto valido');
      return;
    }

    try {
      setPaymentLoading(true);

      await axios.post(`${API}/properties/${editingLicense}/payment`, {
        amount: Math.round(parsedAmount),
        paid: true,
      }, withAdminAuth());

      toast.success('Pago registrado exitosamente');
      setLastActionMessage(`[${nowLabel()}] Pago registrado para ${editingLicense}`);
      resetPaymentDialog();
      fetchProperties();
    } catch (error) {
      console.error('Error updating payment:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleUnmarkPaid = async (licenseNumber) => {
    if (!window.confirm('Desmarcar como pagado?')) return;

    try {
      await axios.post(`${API}/properties/${licenseNumber}/unpay`, {}, withAdminAuth());
      toast.success('Marcado como no pagado');
      setLastActionMessage(`[${nowLabel()}] Propiedad ${licenseNumber} marcada como no pagada`);
      fetchProperties();
    } catch (error) {
      console.error('Error unmarking as paid:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'Error al desmarcar');
    }
  };

  const handleGenerateZip = async (licenseNumber) => {
    try {
      toast.info('Generando ZIP...');
      const response = await axios.get(`${API}/admin/generate-zip/${licenseNumber}`, withAdminAuth({
        responseType: 'blob',
      }));

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${licenseNumber}-website.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('ZIP generado y descargado');
      setLastActionMessage(`[${nowLabel()}] ZIP generado para ${licenseNumber}`);
    } catch (error) {
      console.error('Error generating ZIP:', error);
      if (handleAuthError(error)) return;
      toast.error('Error al generar ZIP');
    }
  };

  const loadManageLink = useCallback(async (licenseNumber) => {
    const cached = manageLinksByLicense[licenseNumber];
    if (cached?.manage_url) return cached.manage_url;

    try {
      setBusyManageLinkFor(licenseNumber);
      const response = await axios.get(`${API}/modifications/admin/${licenseNumber}/manage-link`, withAdminAuth());
      setManageLinksByLicense((current) => ({
        ...current,
        [licenseNumber]: response.data,
      }));
      return response.data.manage_url;
    } catch (error) {
      console.error('Error loading manage link:', error);
      if (handleAuthError(error)) return null;
      toast.error(error.response?.data?.detail || 'No se pudo obtener el enlace privado');
      return null;
    } finally {
      setBusyManageLinkFor('');
    }
  }, [handleAuthError, manageLinksByLicense, withAdminAuth]);

  const handleCopyManageLink = async (licenseNumber) => {
    const manageUrl = await loadManageLink(licenseNumber);
    if (!manageUrl) return;

    try {
      await navigator.clipboard.writeText(manageUrl);
      toast.success('Enlace privado copiado');
      setLastActionMessage(`[${nowLabel()}] Enlace privado copiado para ${licenseNumber}`);
    } catch (error) {
      console.error('Error copying manage link:', error);
      toast.error('No se pudo copiar el enlace al portapapeles');
    }
  };

  const handleResendManageLink = async (licenseNumber) => {
    try {
      setBusyResendFor(licenseNumber);
      const response = await axios.post(`${API}/modifications/admin/${licenseNumber}/resend-manage-link`, {}, withAdminAuth());
      if (response.data?.manage_url) {
        setManageLinksByLicense((current) => ({
          ...current,
          [licenseNumber]: {
            ...(current[licenseNumber] || {}),
            manage_url: response.data.manage_url,
          },
        }));
      }
      toast.success('Enlace privado reenviado al cliente');
      setLastActionMessage(`[${nowLabel()}] Enlace privado reenviado para ${licenseNumber}`);
    } catch (error) {
      console.error('Error resending manage link:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'No se pudo reenviar el enlace privado');
    } finally {
      setBusyResendFor('');
    }
  };

  const handlePublishPendingVersion = async (versionItem) => {
    if (!versionItem?.id) return;
    if (!window.confirm(`Marcar la version v${versionItem.version_number} como publicada?`)) return;

    try {
      setBusyVersionPublishId(versionItem.id);
      await axios.post(`${API}/modifications/admin/versions/${versionItem.id}/publish`, {}, withAdminAuth());
      toast.success(`Version v${versionItem.version_number} marcada como publicada`);
      setLastActionMessage(`[${nowLabel()}] Version v${versionItem.version_number} publicada para ${versionItem.license_number}`);
      fetchProperties();
    } catch (error) {
      console.error('Error publishing pending version:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'No se pudo marcar la version como publicada');
    } finally {
      setBusyVersionPublishId(null);
    }
  };

  const handleRecreateSampleData = async () => {
    if (!window.confirm('Esto actualizara o recreara los datos de ejemplo. Deseas continuar?')) return;

    try {
      setRecreatingSample(true);
      const response = await axios.post(`${API}/admin/recreate-sample-data`, {}, withAdminAuth());
      toast.success(response.data?.message || 'Datos de ejemplo recreados');
      setLastActionMessage(`[${nowLabel()}] Datos de muestra ${response.data?.action === 'created' ? 'creados' : 'actualizados'}`);
      fetchProperties();
    } catch (error) {
      console.error('Error recreating sample data:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'Error al recrear datos de ejemplo');
    } finally {
      setRecreatingSample(false);
    }
  };

  const handleApproveReview = async (reviewId) => {
    try {
      setBusyReviewId(reviewId);
      await axios.post(`${API}/reviews/admin/${reviewId}/approve`, {}, withAdminAuth());
      toast.success('Resena aprobada');
      setLastActionMessage(`[${nowLabel()}] Resena ${reviewId} aprobada`);
      fetchProperties();
    } catch (error) {
      console.error('Error approving review:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'No se pudo aprobar la resena');
    } finally {
      setBusyReviewId(null);
    }
  };

  const handleRejectReview = async (reviewId) => {
    try {
      setBusyReviewId(reviewId);
      await axios.post(`${API}/reviews/admin/${reviewId}/reject`, {}, withAdminAuth());
      toast.success('Resena rechazada');
      setLastActionMessage(`[${nowLabel()}] Resena ${reviewId} rechazada`);
      fetchProperties();
    } catch (error) {
      console.error('Error rejecting review:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'No se pudo rechazar la resena');
    } finally {
      setBusyReviewId(null);
    }
  };

  const handleToggleFeaturedReview = async (reviewId, featured) => {
    try {
      setBusyReviewId(reviewId);
      await axios.post(`${API}/reviews/admin/${reviewId}/feature`, { featured }, withAdminAuth());
      toast.success(featured ? 'Resena destacada' : 'Resena ya no destacada');
      setLastActionMessage(`[${nowLabel()}] Resena ${reviewId} ${featured ? 'destacada' : 'actualizada'}`);
      fetchProperties();
    } catch (error) {
      console.error('Error toggling review featured:', error);
      if (handleAuthError(error)) return;
      toast.error(error.response?.data?.detail || 'No se pudo actualizar la resena');
    } finally {
      setBusyReviewId(null);
    }
  };

  const handleLogout = () => {
    clearAdminSession();
    navigate('/admin/login');
  };

  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);

    const nextParams = new URLSearchParams(searchParams);
    if (tabValue === 'reviews') {
      nextParams.set('tab', 'reviews');
    } else {
      nextParams.delete('tab');
      nextParams.delete('review_id');
    }
    setSearchParams(nextParams, { replace: true });
  };

  const getTextValue = (value) => {
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      if (typeof value.es === 'string') return value.es;
      if (typeof value.en === 'string') return value.en;
      const firstValue = Object.values(value).find((item) => typeof item === 'string');
      return firstValue || '';
    }
    return '';
  };

  const isPropertyPublished = useCallback((property) => {
    return property?.status === 'published' || Boolean(property?.publication?.published);
  }, []);

  const getStatusBadge = (property) => {
    if (isPropertyPublished(property)) return <span className="inline-flex items-center rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white">Publicado</span>;
    if (property.payment?.paid) return <span className="inline-flex items-center rounded-md bg-blue-600 px-2 py-1 text-xs font-medium text-white">Pagado</span>;
    return <span className="inline-flex items-center rounded-md bg-gray-200 px-2 py-1 text-xs font-medium text-gray-700">Borrador</span>;
  };

  const getPlanBadge = (plan) => {
    const colors = {
      basic: 'bg-gray-600',
      plus: 'bg-purple-600',
      premium: 'bg-amber-600',
    };
    return <span className={`inline-flex items-center rounded-md ${colors[plan] || 'bg-gray-600'} px-2 py-1 text-xs font-medium text-white`}>{String(plan || '').toUpperCase()}</span>;
  };

  const filteredProperties = useMemo(() => {
    return properties.filter((property) => {
      const propertyName = getTextValue(property.property_data?.name);
      const matchesSearch =
        property.license_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        propertyName.toLowerCase().includes(searchTerm.toLowerCase());

      const publicationRequested = property.payment?.paid && !isPropertyPublished(property);
      const pendingVersionCount = (pendingVersionsByLicense[property.license_number] || []).length;
      const hasPendingRequests = publicationRequested || pendingVersionCount > 0;

      const matchesFilter =
        filterStatus === 'all' ||
        (filterStatus === 'paid' && property.payment?.paid) ||
        (filterStatus === 'published' && isPropertyPublished(property)) ||
        (filterStatus === 'draft' && !property.payment?.paid) ||
        (filterStatus === 'requests' && hasPendingRequests);

      return matchesSearch && matchesFilter;
    });
  }, [filterStatus, isPropertyPublished, pendingVersionsByLicense, properties, searchTerm]);

  const stats = useMemo(() => ({
    total: properties.length,
    paid: properties.filter((item) => item.payment?.paid).length,
    published: properties.filter((item) => isPropertyPublished(item)).length,
    revenue: properties
      .filter((item) => item.payment?.paid)
      .reduce((sum, item) => sum + (item.payment?.amount || 0), 0),
  }), [isPropertyPublished, properties]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando propiedades...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminDashboardHeader
        lastActionMessage={lastActionMessage}
        recreatingSample={recreatingSample}
        onRecreateSampleData={handleRecreateSampleData}
        onLogout={handleLogout}
      />

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
          <TabsList>
            <TabsTrigger value="properties">Propiedades</TabsTrigger>
            <TabsTrigger value="reviews">Resenas</TabsTrigger>
          </TabsList>

          <TabsContent value="properties" className="space-y-6">
            <AdminStatsCards stats={stats} />

            <AdminFiltersBar
              searchTerm={searchTerm}
              filterStatus={filterStatus}
              onSearchChange={setSearchTerm}
              onFilterChange={setFilterStatus}
            />

            <div className="space-y-4">
              {filteredProperties.length === 0 ? (
                <Card>
                  <CardContent className="py-8 text-center text-gray-500">No se encontraron propiedades</CardContent>
                </Card>
              ) : (
                filteredProperties.map((property) => {
                  const publicationRequested = property.payment?.paid && !isPropertyPublished(property);
                  const modificationItems = modificationRequestsByLicense[property.license_number] || [];
                  const pendingVersionItems = pendingVersionsByLicense[property.license_number] || [];
                  const versionItems = versionsByLicense[property.license_number] || [];
                  const hasAnyRequest = publicationRequested || pendingVersionItems.length > 0;

                  return (
                    <AdminPropertyCard
                      key={property.license_number}
                      property={property}
                      modificationItems={modificationItems}
                      pendingVersionItems={pendingVersionItems}
                      versionItems={versionItems}
                      publicationRequested={publicationRequested}
                      hasAnyRequest={hasAnyRequest}
                      busyVersionPublishId={busyVersionPublishId}
                      getTextValue={getTextValue}
                      getStatusBadge={getStatusBadge}
                      getPlanBadge={getPlanBadge}
                      busyManageLinkFor={busyManageLinkFor}
                      busyResendFor={busyResendFor}
                      onOpenPreview={() => window.open(`/preview/${property.license_number}`, '_blank')}
                      onEditData={() => navigate(`/submit/${property.license_number}/edit`)}
                      onClone={() => navigate(`/submit?cloneFrom=${encodeURIComponent(property.license_number)}`)}
                      onPublish={() => openPublishDialog(property.license_number)}
                      onViewSite={() => window.open(property.publication?.netlify_url, '_blank')}
                      onEditPublicationUrl={() => openPublishDialog(property.license_number)}
                      onUnpublish={() => handleUnpublish(property.license_number)}
                      onRegisterPayment={() => openPaymentDialog(property.license_number)}
                      onEditPayment={() => openPaymentDialog(property.license_number)}
                      onUnmarkPaid={() => handleUnmarkPaid(property.license_number)}
                      onGenerateZip={() => handleGenerateZip(property.license_number)}
                      onCopyManageLink={() => handleCopyManageLink(property.license_number)}
                      onResendManageLink={() => handleResendManageLink(property.license_number)}
                      onPublishPendingVersion={handlePublishPendingVersion}
                      onDelete={() => handleDelete(property.license_number)}
                    />
                  );
                })
              )}
            </div>
          </TabsContent>

          <TabsContent value="reviews" className="space-y-6">
            <AdminReviewsPanel
              reviews={reviews}
              loading={loading}
              busyReviewId={busyReviewId}
              onApprove={handleApproveReview}
              onReject={handleRejectReview}
              onToggleFeatured={handleToggleFeaturedReview}
            />
          </TabsContent>
        </Tabs>
      </div>

      <PublicationDialog
        open={publishDialogOpen}
        editingLicense={editingLicense}
        publishingProperty={publishingProperty}
        publishUrl={publishUrl}
        publishLoading={publishLoading}
        onOpenChange={setPublishDialogOpen}
        onPublishUrlChange={setPublishUrl}
        onCancel={resetPublishDialog}
        onSave={handleSavePublication}
      />

      <PaymentDialog
        open={paymentDialogOpen}
        paymentAmount={paymentAmount}
        paymentLoading={paymentLoading}
        onOpenChange={setPaymentDialogOpen}
        onPaymentAmountChange={setPaymentAmount}
        onCancel={resetPaymentDialog}
        onSave={handleSavePayment}
      />
    </div>
  );
};
